import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { ChromePicker } from 'react-color';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    IconButton,
    InputAdornment,
    Popover,
    Avatar,
    Box
} from "@material-ui/core";

import { Colorize, CloudUpload } from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getProduct, createProduct, updateProduct } from "../../services/products";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    imagePreview: {
        width: 120,
        height: 120,
        margin: "10px auto",
    },
    uploadButton: {
        marginTop: theme.spacing(2),
    }
}));

const ProductSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "Nome muito curto")
        .required("Obrigatório"),
});

const ProductModal = ({ open, onClose, productId, reload }) => {
    const classes = useStyles();
    const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const initialState = {
        name: "",
        price: "",
        color: "#000000",
        description: "",
    };

    const [product, setProduct] = useState(initialState);

    useEffect(() => {
        const fetchProduct = async () => {
            if (!productId) {
                setProduct(initialState);
                setImagePreview(null);
                setImageFile(null);
                return;
            }

            try {
                const { data } = await getProduct(productId);
                setProduct({
                    name: data.name || "",
                    price: data.price || "",
                    color: data.color || "#000000",
                    description: data.description || "",
                });
                if (data.image) {
                    setImagePreview(`${getBackendUrl()}${data.image}`);
                }
            } catch (err) {
                toastError(err);
            }
        };
        fetchProduct();
    }, [productId, open]);

    const handleClose = () => {
        setProduct(initialState);
        setColorPickerAnchor(null);
        setImageFile(null);
        setImagePreview(null);
        onClose();
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProduct = async (values) => {
        const formData = new FormData();
        formData.append("name", values.name);
        if (values.price) formData.append("price", values.price);
        if (values.color) formData.append("color", values.color);
        if (values.description) formData.append("description", values.description);
        if (imageFile) formData.append("image", imageFile);

        try {
            if (productId) {
                await updateProduct(productId, formData);
            } else {
                await createProduct(formData);
            }
            toast.success(i18n.t("products.toasts.success"));
            if (reload) reload();
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {productId
                        ? i18n.t("products.modal.edit")
                        : i18n.t("products.modal.add")}
                </DialogTitle>
                <Formik
                    initialValues={product}
                    enableReinitialize={true}
                    validationSchema={ProductSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setTimeout(() => {
                            handleSaveProduct(values);
                            setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Box display="flex" flexDirection="column">
                                    {imagePreview && (
                                        <Avatar
                                            src={imagePreview}
                                            alt="Preview"
                                            className={classes.imagePreview}
                                            variant="rounded"
                                        />
                                    )}
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="image-upload"
                                        type="file"
                                        onChange={handleImageChange}
                                    />
                                    <label htmlFor="image-upload">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            startIcon={<CloudUpload />}
                                            className={classes.uploadButton}
                                            fullWidth
                                        >
                                            {i18n.t("products.modal.form.image")}
                                        </Button>
                                    </label>

                                    <Field
                                        as={TextField}
                                        label={i18n.t("products.modal.form.name")}
                                        name="name"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("products.modal.form.price")}
                                        name="price"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        error={touched.price && Boolean(errors.price)}
                                        helperText={touched.price && errors.price}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("products.modal.form.color")}
                                        name="color"
                                        error={touched.color && Boolean(errors.color)}
                                        helperText={touched.color && errors.color}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <div style={{ backgroundColor: values.color, width: 20, height: 20, borderRadius: 4 }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <IconButton
                                                    size="small"
                                                    color="default"
                                                    onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                                                >
                                                    <Colorize />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                    <Field
                                        as={TextField}
                                        label="Descrição"
                                        name="description"
                                        multiline
                                        rows={3}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Popover
                                        open={Boolean(colorPickerAnchor)}
                                        anchorEl={colorPickerAnchor}
                                        onClose={() => setColorPickerAnchor(null)}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                    >
                                        <ChromePicker
                                            color={values.color}
                                            disableAlpha={true}
                                            onChangeComplete={(color) => {
                                                setFieldValue("color", color.hex);
                                            }}
                                        />
                                    </Popover>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {productId
                                        ? i18n.t("global.buttons.save")
                                        : i18n.t("global.buttons.add")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default ProductModal;
